import { useState, useEffect } from 'react'
import { Col, Row, Card, Form, Button } from 'react-bootstrap';
import { Spinner } from 'reactstrap';
import Select from 'react-select';
import { useForm, useFieldArray, Controller } from 'react-hook-form';
import { useParams, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';

// Custom Component
import SingleUploadDocumentCard from './SingleUploadDocumentCard';
import SubmissionEndModal from '../modal/SubmissionEndModal';

// Redux-Functions
import { selectLanguageUse } from '../../../redux/slices/language.Slice'
import { useSelector } from 'react-redux';

// Utility-Service
import { getToken, getUser } from "../../../utility/AuthService";
import { getActiveDimensionListService } from '../../../services/dimensionService';

// API-Service
import { uploadDocumentServices, uploadPDFServices } from '../../../services/uploadDocumentServices/uploadDocumentServices';

const UploadDocuments = () => {
    const { id } = useParams();
    const navigate = useNavigate();

    const authToken = getToken();
    const [modalShow, setModalShow] = useState(false);

    const [dimensions, setDimensions] = useState([]);
    const [dimensionsFrench, setDimensionsFrench] = useState([]);
    const [isLoading, setLoading] = useState(false);
    const [isUploading, setIsUploading] = useState(false);
    const [isUploadComplete, setIsUploadComplete] = useState(false);
    const [hasDocuments, setHasDocuments] = useState(false);

    const user = getUser();
    const languageUse = useSelector(selectLanguageUse || user?.language);

    const handleModalShow = () => setModalShow(!modalShow);

    const { register, setValue, handleSubmit, watch, control, formState: { errors } } = useForm(
        {
            defaultValues: {
                inputs: [
                    {
                        Dimension: "",
                        document_Name: "",
                        Description: "",
                        uploadPDF: [{ files: null }]
                    }
                ]
            }
        }
    );

    const { fields: option_fields, append: option_append, remove: option_remove } = useFieldArray({
        control,
        name: "inputs"
    });

    const watchDocument = watch('inputs')
    const allWatchedValues = watch({ nest: true });

    useEffect(() => {
        // Check if assessment is complete
        const isAssessmentComplete = localStorage.getItem(`assessment_complete_${id}`);
        if (!isAssessmentComplete) {
            // Show warning but don't block - allow user to try uploading
            // The backend will validate completion status
            toast.dismiss();
            toast.error(languageUse === 'English' 
                ? '⚠️ Please ensure all assessment sections are completed before uploading documents. You can still proceed, but incomplete assessments may not be accepted.' 
                : '⚠️ Veuillez vous assurer que toutes les sections de l\'évaluation sont complétées avant de télécharger des documents. Vous pouvez toujours continuer, mais les évaluations incomplètes peuvent ne pas être acceptées.',
                { duration: 6000 }
            );
            // Don't navigate away - let user proceed if they want
            // navigate(-1);
        }
    }, [id, navigate]);

    useEffect(() => {
        // Check if there are any documents with files
        const hasUploadedFiles = watchDocument?.some(doc => 
            doc?.uploadPDF?.some(pdf => pdf?.files !== null)
        );
        setHasDocuments(hasUploadedFiles);
        
        // Reset upload complete state when documents change
        if (hasUploadedFiles) {
            setIsUploadComplete(false);
        }
    }, [watchDocument]);

    /**
        * @function getDimensionList
        * @param ()
        * @description to get Dimension List
    */
    const getDimensionList = async () => {
        setLoading(true)
        try {
            const result = await getActiveDimensionListService({
                authToken: authToken,
            });
            if (result?.data?.statusCode === 200) {
                const dimension = []
                result?.data?.data?.list?.map(i => {
                    dimension.push({ label: i?.name, value: i?._id })
                });
                dimension?.shift()

                const dimensionFrench = []
                result?.data?.data?.list?.map(i => {
                    dimensionFrench.push({ label: i?.name_French, value: i?._id })
                });
                dimensionFrench?.shift()
                setDimensions(dimension)
                setDimensionsFrench(dimensionFrench)
                setLoading(false)
            } else {
                setLoading(false)
                setDimensions([])
                setDimensionsFrench([])
                toast.error(result.data.errorMessage || 'Something went wrong!')
            };
            return false;
        } catch (error) {
            setLoading(false)
            setDimensions([])
            setDimensionsFrench([])
            toast.error(error?.response?.data?.message || 'Something went wrong!')
        }
    }

    useEffect(() => {
        getDimensionList()
    }, [])

    /**
        * @function handleUploadPDF
        * @param index, id
        * @description to upload PDF
    */
    const handleUploadPDF = async (index, id) => {
        setLoading(true)
        const formData = new FormData();

        if (watchDocument?.length > 0) {
            watchDocument[index]?.uploadPDF?.filter(item => item?.files).map((file) => {
                formData.append(`files`, file?.files[0]);
            })
        };

        try {
            const result = await uploadPDFServices({
                authToken: authToken,
                id: id,
                files: formData
            });
            if (result?.data?.statusCode === 200) {
                setModalShow(true)
            } else {
                setLoading(false)
                toast.error(result.data.errorMessage || 'Something went wrong!')
            };
            return false;
        } catch (error) {
            setLoading(false)
            toast.error(error?.response?.data?.message || 'Something went wrong!')
        }
    }

    /**
        * @function handleUploadDoc
        * @param data
        * @description to upload image
    */
    const handleUploadDoc = async (data) => {
        if (!hasDocuments) {
            toast.error(languageUse === 'English' 
                ? 'Please upload at least one document' 
                : 'Veuillez télécharger au moins un document');
            return;
        }

        setIsUploading(true);
        try {
            await Promise.all(watchDocument?.map(async (item, index) => {
                if (!item?.uploadPDF?.some(pdf => pdf?.files !== null)) {
                    return;
                }
                const result = await uploadDocumentServices({
                    authToken: authToken,
                    submissionId: id,
                    dimension: item?.Dimension?.value,
                    name: item?.document_Name,
                    description: item?.Description,
                });
                if (result?.data?.statusCode === 200) {
                    await handleUploadPDF(index, result?.data?.data?._id);
                } else {
                    throw new Error(result.data.errorMessage || 'Something went wrong!');
                }
            }));
            setIsUploadComplete(true);
            setModalShow(true);
        } catch (error) {
            toast.error(error?.message || 'Something went wrong!');
        } finally {
            setIsUploading(false);
        }
    };

    /**
        * @function handleDelete
        * @param id
        * @description to handle delete
    */
    const handleDelete = (id) => {
        option_remove(id)
    }

    /**
        * @function onSubmit
        * @param data
        * @description to submit
    */
    const onSubmit = async (data) => {
        if (!hasDocuments) {
            toast.error(languageUse === 'English' 
                ? 'Please upload at least one document' 
                : 'Veuillez télécharger au moins un document');
            return;
        }

        const confirmed = window.confirm(
            languageUse === 'English' 
                ? 'Are you sure you want to submit the assessment?' 
                : 'Êtes-vous sûr de vouloir soumettre l\'évaluation?'
        );
        
        if (confirmed) {
            await handleUploadDoc(data);
        }
    };

    return <>
        <div className='container pb-3'>
            {!localStorage.getItem(`assessment_complete_${id}`) && (
                <div className='alert alert-warning my-3'>
                    {languageUse === 'English'
                        ? '⚠️ Warning: Please ensure all assessment sections are completed before uploading documents. Incomplete assessments may not be accepted by the system.'
                        : '⚠️ Avertissement : Veuillez vous assurer que toutes les sections de l\'évaluation sont complétées avant de télécharger des documents. Les évaluations incomplètes peuvent ne pas être acceptées par le système.'}
                </div>
            )}
            <Form onSubmit={handleSubmit(onSubmit)} className='h-100'>
                <Row>
                    <div className="d-flex justify-content-between align-items-center mb-4">
                        <h2>{languageUse === 'English' ? 'Upload Documents' : 'Télécharger des documents'}</h2>
                        <div className="upload-status">
                            {isUploadComplete && (
                                <span className="text-success">
                                    <i className="bi bi-check-circle-fill me-2"></i>
                                    {languageUse === 'English' ? 'Documents Uploaded' : 'Documents téléchargés'}
                                </span>
                            )}
                        </div>
                    </div>
                    <p>{languageUse === 'English' 
                        ? "In this section, you can upload documents to provide evidence for your answers. This is important to increase the reliability of your assessment. Supporting documents are for example: a company policy or manual, ISO certification, or training certificates. If you do not have supporting documents, you must provide a document to validate your account. For example, your business card or a screenshot of your company's website." 
                        : "Dans cette section, vous pouvez télécharger des documents pour fournir des preuves de vos réponses. Ceci est important pour augmenter la fiabilité de votre évaluation. Les documents justificatifs peuvent être par exemple : des politiques ou manuels d'entreprise, des certifications ISO ou des certificats de formation. Si vous n'avez pas de documents justificatifs, vous devez fournir un document pour valider votre compte. Par exemple, votre carte de visite ou une capture d'écran du site web de votre entreprise."}
                    </p>
                    {option_fields.map((field, index) => (
                        <Col md={6} sm={12} >
                            <Card className='border p-3 mb-3'>
                                <h4>{languageUse === 'English' ? 'Supporting Document' : 'Document Justificatif'} {index + 1}</h4>
                                <div className='d-flex flex-column justify-content-center align-items-start mt-2 mb-3'>
                                    <label htmlFor="Dimension" className="form-label">
                                        {languageUse === 'English' ? 'Dimension' : 'Dimension'}
                                    </label>
                                    <Controller
                                        name={`inputs.${index}.Dimension`}
                                        control={control}
                                        render={({ field }) => (
                                            <Select
                                                className={'w-100'}
                                                options={languageUse === 'English' ? dimensions : dimensionsFrench}
                                                {...field}
                                            />
                                        )}
                                        rules={{
                                            required: languageUse === 'English' ? 'Dimension is required' : 'La dimension est requise'
                                        }}
                                    />
                                    {errors.inputs?.[index]?.Dimension && <p className='pt-1 text-danger'>{errors.inputs?.[index]?.Dimension?.message}</p>}
                                </div>
                                <div className="mb-3">
                                    <label htmlFor="document_Name" className="form-label">
                                        {languageUse === 'English' ? 'Document Name' : 'Nom du Document'}
                                    </label>
                                    <input
                                        type="text"
                                        className={`form-control ${errors.document_Name ? 'is-invalid' : ''}`}
                                        {...register(`inputs.${index}.document_Name`, {
                                            required: languageUse === 'English' ? 'Document Name is required' : 'Le nom du document est requis'
                                        })}
                                    />
                                    {errors.inputs?.[index]?.document_Name && <p className='pt-1 text-danger'>{errors.inputs?.[index]?.document_Name?.message}</p>}
                                </div>

                                <div className="mb-3">
                                    <label htmlFor="Description" className="form-label">
                                        {languageUse === 'English' ? 'Description' : 'Description'}
                                    </label>
                                    <input
                                        type="text"
                                        className={`form-control ${errors.Description ? 'is-invalid' : ''}`}
                                        {...register(`inputs.${index}.Description`, {
                                            required: languageUse === 'English' ? 'Description is required' : 'La description est requise'
                                        })}
                                    />
                                    {errors.inputs?.[index]?.Description && <p className='pt-1 text-danger'>{errors.inputs?.[index]?.Description?.message}</p>}
                                </div>
                                <SingleUploadDocumentCard nestIndex={index} allWatchedValues={allWatchedValues} {...{ control, register, setValue, errors }} />
                                <div className='mb-2 d-flex justify-content-between'>
                                    <Button className="float-end authButton login-btn-min-width" variant='outline-primary' color="primary" type="button" onClick={() => handleDelete(index)}>
                                        {isLoading ? <Spinner variant='dark' size='sm' /> : languageUse === 'English' ? 'Remove' : 'Supprimer'}
                                    </Button>
                                </div>
                            </Card>
                        </Col>
                    ))}
                </Row>
                <div className='d-flex flex-column'>
                    <Button type="button" className="float-start authButton w-50 my-4" variant='outline-primary' onClick={() => option_append(
                        {
                            Dimension: "",
                            document_Name: "",
                            Description: "",
                            uploadPDF: [{ files: null }]
                        }
                    )}>
                        {languageUse === 'English' ? '+ Add more documents' : '+ Ajouter plus de documents'}
                    </Button>
                    <div className='d-flex justify-content-between mt-4'>
                        <Button 
                            type="button" 
                            className="authButton" 
                            variant='outline-primary' 
                            onClick={() => navigate(-1)}
                        >
                            {languageUse === 'English' ? 'Back' : 'Retour'}
                        </Button>
                        <Button 
                            className='authButton' 
                            type="submit" 
                            variant='outline-primary' 
                            color='primary'
                            disabled={isUploading || !hasDocuments}
                        >
                            {isUploading ? (
                                <Spinner size="sm" />
                            ) : !hasDocuments ? (
                                languageUse === 'English' ? 'Upload Documents First' : 'Téléchargez d\'abord les documents'
                            ) : (
                                languageUse === 'English' ? 'Submit Assessment' : 'Soumettre l\'évaluation'
                            )}
                        </Button>
                    </div>
                </div>
            </Form>
        </div>

        <SubmissionEndModal show={modalShow} handleClose={handleModalShow} submission_id={id} />
    </>
};

export default UploadDocuments;